import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';

import { currentUserSelector } from '../../ducks/user.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, Page, UserNav, LayoutSideNavigation } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import ShippingDetailsForm from './ShippingDetailsForm/ShippingDetailsForm';
import {
  getShippingDetails,
  getShippingDimenisions,
  getShippingPreference,
  getUserEmail,
  getUserFullName,
  getUserPhoneNumber,
} from '../../util/dataExtractor';
import getCountryCodes from '../../translations/countryCodes';
import { createShippoAddress, selectShippoAddressStatus } from '../../ducks/shippo.duck';
import { OWN, PLATFORM } from '../../util/constants';
import { WeightUnitEnum } from '../../config/configExtras';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { showErrorToast, showSuccessToast } from '../../util/toast';

import css from './ShippingDetailsPage.module.css';

// Data model note:
// - profile.protectedData.shippingAddress: { name, street1, street2, city, state, zip, country, phone, objectId }
// - profile.publicData.shippingDimensions: { length, width, height, weight, unit }
// - profile.publicData.shippingPreference + profile.protectedData.shippingPreference: 'platform' | 'own'
// - profile.publicData.addressObjectId: Shippo address object id
export const ShippingDetailsPage = () => {
  const config = useConfiguration();
  const intl = useIntl();
  const dispatch = useDispatch();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const currentUser = useSelector(currentUserSelector);
  const user = ensureCurrentUser(currentUser);

  const { saveShippoAddressInProgress, saveShippoAddressError, shippoAddressSaved } = useSelector(
    selectShippoAddressStatus
  );
  const { updateInProgress: updateProfileInProgress, updateProfileError } = useSelector(
    state => state.ProfileSettingsPage
  );

  const [preferenceSaveRequested, setPreferenceSaveRequested] = useState(false);
  const [preferenceSaveSucceeded, setPreferenceSaveSucceeded] = useState(false);

  const shippingAddress = getShippingDetails(currentUser);
  const shippingDimensions = getShippingDimenisions(currentUser);
  const shippingPreference = getShippingPreference(currentUser);

  const countryCodes = getCountryCodes(config.localization.locale);
  const savedCountry = countryCodes.find(c => c.code === shippingAddress?.country);
  const defaultCountry = savedCountry || countryCodes.find(c => c.code === 'US') || countryCodes[0];
  const savedWeightUnit =
    WeightUnitEnum.find(c => c.value === shippingDimensions?.unit) || WeightUnitEnum[0];

  const currentUserName = getUserFullName(currentUser);
  const currentUserEmail = getUserEmail(currentUser);
  const currentUserPhoneNumber = getUserPhoneNumber(currentUser);

  useEffect(() => {
    if (!preferenceSaveRequested || updateProfileInProgress) {
      return;
    }

    if (updateProfileError) {
      showErrorToast('Failed to save shipping preference');
      setPreferenceSaveSucceeded(false);
    } else {
      showSuccessToast('Shipping preference saved');
      setPreferenceSaveSucceeded(true);
    }

    setPreferenceSaveRequested(false);
  }, [preferenceSaveRequested, updateProfileInProgress, updateProfileError]);

  const initialValues = {
    ...shippingAddress,
    country: defaultCountry,
    length: shippingDimensions?.length,
    width: shippingDimensions?.width,
    height: shippingDimensions?.height,
    weight: shippingDimensions?.weight,
    unit: savedWeightUnit,
    phone: currentUserPhoneNumber,
    shippingPreference: shippingPreference || PLATFORM,
  };

  const handleSubmit = async values => {
    const preference = values?.shippingPreference;
    if (!preference) {
      return;
    }

    if (preference === OWN) {
      setPreferenceSaveSucceeded(false);
      setPreferenceSaveRequested(true);
      await dispatch(
        updateProfile({
          publicData: { shippingPreference: OWN },
          protectedData: { shippingPreference: OWN },
        })
      );
      return;
    }

    const shippingDetails = {
      ...values,
      shippingPreference: PLATFORM,
      email: currentUserEmail,
      name: currentUserName,
      currentUserId: currentUser?.id?.uuid,
      country: values?.country?.code,
      unit: values?.unit?.value,
    };

    await dispatch(createShippoAddress(shippingDetails));
  };

  const saveInProgress = saveShippoAddressInProgress || updateProfileInProgress;
  const saveReady = shippoAddressSaved || preferenceSaveSucceeded;

  const shippingDetailsInfoForm = user.id ? (
    <ShippingDetailsForm
      className={css.form}
      hideTitle
      initialValues={initialValues}
      currentUser={currentUser}
      onSubmit={handleSubmit}
      inProgress={saveInProgress}
      ready={saveReady}
      updateProfileError={saveShippoAddressError}
    />
  ) : null;

  const title = intl.formatMessage({ id: 'ShippingDetailsPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation
        topbar={
          <>
            <TopbarContainer desktopClassName={css.desktopTopbar} mobileClassName={css.mobileTopbar} />
            <UserNav currentPage="ShippingDetailsPage" currentUser={currentUser} />
          </>
        }
        sideNav={null}
        useAccountSettingsNav
        currentPage="ShippingDetailsPage"
        footer={<FooterContainer />}
      >
        <div className={css.content}>
          <H3 as="h1">
            <FormattedMessage id="ShippingDetailsPage.heading" />
          </H3>
          {shippingDetailsInfoForm}
        </div>
      </LayoutSideNavigation>
    </Page>
  );
};

export default ShippingDetailsPage;
